import React, {createContext, useCallback, useContext, useReducer, useMemo, useEffect} from "react";
import {RequestState, TagUser} from "../models";
import {LoadingState, isValidEmail, notEmpty} from "../helper/utils";
import {create} from "../services/requests";

const STORAGE_KEY = "create_request_state";
const minChar = (n: number, v?: string) => notEmpty(v) && (v?.length ?? 0) > n;
const Context = createContext<ReturnType<typeof useRequestValue> | null>(null);
const initialState: State = {
    receivers: [],
    loadingState: LoadingState.None,
    requestState: "not-sent"
};

interface PersistedState {
    decider?: string;
    loadID?: string;
    receivers: TagUser[];
    title?: string;
    description?: string;
}
interface State {
    decider?: string;
    message?: {desc: string; title: string};
    loadID?: string;
    loadingState: LoadingState;
    receivers: TagUser[];
    requestState: RequestState;
    title?: string;
    description?: string;
    shouldRedirect?: boolean
}
type Action =
| {type: "add_email"; payload: TagUser}
| {type: "remove_email"; payload: string}
| {type: "set_loading_state"; payload: LoadingState}
| {type: "set_receiver"; payload: string[]}
| {type: "set_decider"; payload?: string}
| {type: "set_load_id"; payload: string}
| {type: "set_error"; payload?: {desc: string; title: string}}
| {type: "set_request_state"; payload: RequestState}
| {type: "set_title"; payload: string}
| {type: "set_desc"; payload: string}
| {type: "clear_form"}
| {type: "set_redirect", payload: boolean}
| {type: "hydrate"; payload: PersistedState};

function loadPersistedState(): PersistedState | null {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored) as PersistedState;
        }
    } catch (error) {
        console.error("Failed to load persisted state:", error);
    }
    return null;
}

function savePersistedState(state: State): void {
    try {
        const persistedState: PersistedState = {
            decider: state.decider,
            loadID: state.loadID,
            receivers: state.receivers,
            title: state.title,
            description: state.description
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedState));
    } catch (error) {
        console.error("Failed to save persisted state:", error);
    }
}

function clearPersistedState(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error("Failed to clear persisted state:", error);
    }
}

function createRequestActions(dispatch: React.Dispatch<Action>) {
    return {
        addEmail: (payload: TagUser) => dispatch({type: "add_email", payload}),
        clearError: () => dispatch({type: "set_error"}),
        clearForm: () => dispatch({type: "clear_form"}),
        reject: () => dispatch({type: "set_request_state", payload: "failed"}),
        removeEmail: (payload: string) => dispatch(
            {type: "remove_email", payload}
        ),
        resolve: function () {
            dispatch({type: "set_request_state", payload: "verified"});
        },
        setDecider: (payload?: string) => dispatch({type: "set_decider", payload}),
        setHelper: (payload: string) => dispatch({type: "set_desc", payload}),
        setLoadId: (payload: string) => dispatch({type: "set_load_id", payload}),
        setReceiver: (payload: string[]) => dispatch(
            {type: "set_receiver", payload}
        ),
        setRequeststate: (payload: LoadingState) => dispatch(
            {type: "set_loading_state", payload}
        ),
        setTitle: (payload: string) => dispatch({type: "set_title", payload}),
        setRedirect: (payload: boolean) => dispatch({type: "set_redirect", payload})
    };
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case "set_loading_state": return {...state, loadingState: action.payload};
        case "set_decider": return {...state, decider: action.payload};
        case "set_load_id": return {...state, loadID: action.payload};
        case "set_error": return {...state, message: action.payload};
        case "set_request_state": return {...state, requestState: action.payload};
        case "set_desc": return {...state, description: action.payload};
        case "set_redirect": return {...state, shouldRedirect: action.payload};
        case "clear_form": return {
            ...initialState,
            loadingState: state.loadingState
        };
        case "set_title": return {...state, title: action.payload};
        case "add_email": {
            const receivers = state.receivers.concat([action.payload]);
            if (state.receivers.some((v) => v.email === action.payload.email)) {
                return state;
            }
            return {...state, receivers};
        }
        case "remove_email": {
            const receivers = state.receivers.filter(
                (v) => v.email !== action.payload
            );
            return {...state, receivers};
        }
        case "hydrate": {
            return {...state, ...action.payload};
        }
        default: return state;
    }
}

function useRequestValue() {
    const [state, dispatch] = useReducer(reducer, initialState);
    const actions = useMemo(() => createRequestActions(dispatch), [dispatch]);

    useEffect(function () {
        const persisted = loadPersistedState();
        if (persisted) {
            dispatch({type: "hydrate", payload: persisted});
        }
    }, []);
    useEffect(function () {
        savePersistedState(state);
    }, [state.decider, state.loadID, state.receivers, state.title, state.description]);

    const isValidForm = useMemo(function () {
        const hasReceiver = state.receivers.length > 0;
        const hasApprover = isValidEmail(state.decider ?? null);
        const hasTitle = minChar(5, state.title);
        const hasDesc = minChar(5, state.description);
        return hasReceiver && hasApprover && hasTitle && hasDesc;
    }, [state]);

    /** Create the request: at once, or after the phone approves (when required). */
    const submitRequest = useCallback(async function (): Promise<{ok: boolean, code?: string}> {
        let res;
        if (!isValidForm) {
            return {ok: false};
        }
        actions.setRequeststate(LoadingState.Pending);
        res = await create({
            description: state.description!,
            title: state.title!,
            recipients: state.receivers.map((r) => r.email),
            resolver: state.decider!
        });
        if (res.type === "success") {
            actions.setRequeststate(LoadingState.Success);
            dispatch({type: "set_error", payload: {
                desc: "Your request has been sent.",
                title: "Request Submission Successful"
            }});
            actions.clearForm();
            clearPersistedState();
            dispatch({type: "set_request_state", payload: "verified"});
            return {ok: true};
        }
        actions.setRequeststate(LoadingState.Error);
        if (res.code !== "approval_phone_required") {
            dispatch({type: "set_error", payload: {
                desc: res.message,
                title: "Request Submission Error"
            }});
        }
        return {code: res.code, ok: false};
    }, [actions, isValidForm, state]);
    return useMemo(
        () => ({actions, dispatch, isValidForm, state, submitRequest}),
        [actions, isValidForm, state, submitRequest]
    );
}

export function CreateRequestProvider(props: {children: React.ReactNode}) {
    const value = useRequestValue();
    return <Context.Provider value={value}>
        {props.children}
    </Context.Provider>;
}

export function useCreateRequest() {
    const context = useContext(Context);
    if (!context) {
        throw new Error("useCreateLoad must be used within a CreateLoadProvider");
    }
    return context;
}
